// The Stat Tracker tile on a client's 1-on-1 coaching board (board item type 'statTracker').
// The client sees their own numbers and can connect a CRM or request one; the coach sees the same numbers,
// kept current by the CRM autofill, every time the board opens. The server decides who may see a tile.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import classes from './StatTrackerTile.module.css';
import { connectCrm, CrmOption, getCrms, getTile, requestCrm, saveTileFields, TileData } from './api';

const PACE: Record<string, [string, string]> = { on_pace: ['On pace', 'good'], slightly_behind: ['Slightly behind', 'warn'], behind: ['Behind', 'crit'] };
const num = (v: number) => String(Math.round(v * 100) / 100);
const errText = (e: unknown, fallback: string): string => {
  const m = e && typeof e === 'object' ? (e as { message?: unknown }).message : null;
  return typeof m === 'string' && m ? m : fallback;
};

const Sparkline = ({ values }: { values: number[] }) => {
  const w = 160, h = 34, mx = Math.max(...values, 1);
  const pts = values.map((v, i) => `${(i / Math.max(values.length - 1, 1)) * (w - 6) + 3},${h - 4 - (v / mx) * (h - 10)}`).join(' ');
  return <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden='true'><polyline points={pts} fill='none' stroke='#0b63ce' strokeWidth='2' /></svg>;
};

const StatTrackerTile = ({ widgetId }: { widgetId: number; clientId?: number | null }) => {
  const [tile, setTile] = useState<TileData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connectOpen, setConnectOpen] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    getTile(widgetId).then((t) => { setTile(t); setError(null); }).catch((e) => setError(errText(e, 'Could not load the stats. Try again.')));
  }, [widgetId]);
  useEffect(load, [load]);

  if (error) return <div className={classes.tile}><div className={classes.head}><b>Stat Tracker</b></div><p className={classes.small}>{error}</p><button className={classes.link} onClick={load}>Try again</button></div>;
  if (!tile) return <div className={classes.tile}><div className={classes.head}><b>Stat Tracker</b></div><p className={classes.small}>Loading…</p></div>;

  const first = tile.client.name.split(' ')[0] || 'This client';
  const worst = tile.stats.map((s) => s.pace?.status).filter(Boolean) as string[];
  const overall = worst.includes('behind') ? 'behind' : worst.includes('slightly_behind') ? 'slightly_behind' : worst.length ? 'on_pace' : null;

  return (
    <div className={classes.tile}>
      <div className={classes.head}>
        <div className={classes.grow}>
          <div className={classes.eyebrow}>STAT TRACKER</div>
          <b className={classes.name}>{tile.isClient ? 'My numbers' : tile.client.name}</b>
          <div className={classes.small}>{tile.team ? tile.team.name : 'No Stat Tracker team yet'}</div>
        </div>
        {overall && <span className={`${classes.pill} ${classes[PACE[overall][1]]}`}>{PACE[overall][0]}</span>}
      </div>

      <div className={classes.crmRow}>
        {tile.crm ? (
          <span className={`${classes.pill} ${classes.good}`}>● {tile.crm.name}{tile.lastFilled ? ` · synced ${moment(tile.lastFilled).fromNow()}` : ' · connected'}</span>
        ) : (
          <span className={`${classes.pill} ${classes.mute}`}>No CRM connected</span>
        )}
        {tile.isClient && <button className={classes.link} onClick={() => setConnectOpen(true)}>{tile.crm ? 'Change CRM' : 'Connect API key'}</button>}
      </div>
      {!tile.crm && (tile.isClient
        ? <div className={classes.callout}><b>Connect your CRM once</b> and your calls, conversations, appointments and new leads fill in here every morning, so your coach always sees where you are. <button className={`${classes.btn} ${classes.gold}`} onClick={() => setConnectOpen(true)}>Connect API key</button></div>
        : <p className={classes.small}>{first} has not connected a CRM yet. Numbers they type in the Stat Tracker show here.</p>)}
      {tile.crm && !tile.crm.autoStats && <p className={classes.small}>{tile.crm.name} is connected. Automatic numbers from {tile.crm.name} are being added; until then, typed numbers show here.</p>}
      {notice && <p className={classes.notice} role='status'>{notice}</p>}

      {tile.stats.length > 0 ? (
        <div className={classes.grid}>
          {tile.stats.map((s) => (
            <div key={s.fieldId} className={classes.stat}>
              <div className={classes.small}>{s.name}</div>
              <div className={classes.big}>{num(s.week)}</div>
              <div className={classes.small}>
                this week{s.pace ? ` of ${num(s.pace.goalWeek)}` : ''} · today {num(s.today)}
              </div>
              {s.pace && <div className={classes.meter}><i className={classes[PACE[s.pace.status][1]]} style={{ width: `${Math.min(100, (s.week / Math.max(s.pace.goalWeek, 0.01)) * 100)}%` }} /></div>}
              <div className={classes.small}>last week {num(s.lastWeek)} · month {num(s.month)}</div>
            </div>
          ))}
        </div>
      ) : tile.team ? <p className={classes.small}>No stats picked yet.</p> : null}

      {tile.trend.length > 0 && tile.stats[0] && (
        <div className={classes.trend}><span className={classes.small}>{tile.stats[0].name}, last 8 weeks</span><Sparkline values={tile.trend.map((t) => t.value)} /></div>
      )}

      <div className={classes.footer}>
        {tile.team && <button className={`${classes.btn} ${classes.ghost}`} onClick={() => setChoosing(true)}>Choose stats</button>}
        {tile.isClient && <Link className={`${classes.btn} ${classes.ghost}`} to='/st1/v2'>Open Stat Tracker</Link>}
      </div>

      {choosing && tile.team && (
        <ChooseStats tile={tile} onClose={() => setChoosing(false)} onSaved={() => { setChoosing(false); load(); }} />
      )}
      {connectOpen && (
        <ConnectCrm onClose={() => setConnectOpen(false)} onConnected={(name) => { setConnectOpen(false); setNotice(`${name} is connected. Your numbers will start filling in within a few minutes.`); load(); }} />
      )}
    </div>
  );
};

const ChooseStats = ({ tile, onClose, onSaved }: { tile: TileData; onClose: () => void; onSaved: () => void }) => {
  const [picked, setPicked] = useState<number[]>(tile.chosen || tile.stats.map((s) => s.fieldId));
  const [err, setErr] = useState<string | null>(null);
  const toggle = (id: number) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : picked.length < 6 ? [...picked, id] : picked);
  const save = () => saveTileFields(tile.widgetId, picked).then(onSaved).catch((e) => setErr(errText(e, 'That did not save. Try again.')));
  return (
    <div className={classes.scrim} role='dialog' aria-modal='true' aria-label='Choose stats'>
      <div className={classes.modal}>
        <h3 className={classes.h3}>Stats on this tile</h3>
        <p className={classes.small}>Pick up to six. Your coach and you see the same tile.</p>
        <div className={classes.checks}>
          {tile.fields.map((f) => (
            <label key={f.id} className={classes.check}>
              <input type='checkbox' checked={picked.includes(f.id)} disabled={!picked.includes(f.id) && picked.length >= 6} onChange={() => toggle(f.id)} /> {f.name}
            </label>
          ))}
        </div>
        {err && <p className={classes.err}>{err}</p>}
        <div className={classes.footer}>
          <button className={`${classes.btn} ${classes.ghost}`} onClick={onClose}>Cancel</button>
          <button className={`${classes.btn} ${classes.gold}`} disabled={!picked.length} onClick={save}>Save</button>
        </div>
      </div>
    </div>
  );
};

export const ConnectCrm = ({ onClose, onConnected }: { onClose: () => void; onConnected: (name: string) => void }) => {
  const [crms, setCrms] = useState<CrmOption[] | null>(null);
  const [pick, setPick] = useState<CrmOption | null>(null);
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [askName, setAskName] = useState('');
  const [askNote, setAskNote] = useState('');
  const [asked, setAsked] = useState<string | null>(null);

  useEffect(() => { getCrms().then(setCrms).catch(() => setErr('Could not load the list of CRMs. Try again.')); }, []);

  const connect = async () => {
    if (!pick || !key.trim()) return;
    setBusy(true); setErr(null);
    try { await connectCrm(pick.key, key.trim()); onConnected(pick.name); }
    catch (e) { setErr(errText(e, 'That key did not work. Check it and try again.')); setBusy(false); }
  };
  const ask = async () => {
    setBusy(true); setErr(null);
    try { await requestCrm(askName.trim(), askNote.trim()); setAsked(askName.trim()); }
    catch (e) { setErr(errText(e, 'That did not send. Try again.')); }
    setBusy(false);
  };

  return (
    <div className={classes.scrim} role='dialog' aria-modal='true' aria-label='Connect your CRM'>
      <div className={classes.modal}>
        {asked ? (
          <>
            <h3 className={classes.h3}>Request sent</h3>
            <p>Thanks. We let Client Care know you use <b>{asked}</b>, and we will reach out as soon as it can be connected.</p>
            <div className={classes.footer}><button className={`${classes.btn} ${classes.gold}`} onClick={onClose}>Done</button></div>
          </>
        ) : asking ? (
          <>
            <h3 className={classes.h3}>Request a CRM</h3>
            <p className={classes.small}>Tell us which CRM you use and we will work on adding it.</p>
            <label className={classes.field}><span>CRM name</span><input value={askName} maxLength={60} onChange={(e) => setAskName(e.target.value)} placeholder='For example: Wise Agent' /></label>
            <label className={classes.field}><span>Anything else we should know (optional)</span><textarea value={askNote} maxLength={500} rows={3} onChange={(e) => setAskNote(e.target.value)} /></label>
            {err && <p className={classes.err}>{err}</p>}
            <div className={classes.footer}>
              <button className={`${classes.btn} ${classes.ghost}`} onClick={() => setAsking(false)}>Back</button>
              <button className={`${classes.btn} ${classes.gold}`} disabled={busy || askName.trim().length < 2} onClick={ask}>{busy ? 'Sending…' : 'Send request'}</button>
            </div>
          </>
        ) : !pick ? (
          <>
            <h3 className={classes.h3}>Which CRM do you use?</h3>
            <p className={classes.small}>We only read your activity. We never send messages or change anything in your CRM.</p>
            <div className={classes.crmGrid}>
              {(crms || []).map((c) => (
                <button key={c.key} className={classes.crm} onClick={() => setPick(c)}>
                  <b>{c.name}</b>
                  <span className={classes.small}>{c.autoStats ? 'Fills your stats automatically' : 'Connect now, automatic stats coming'}</span>
                </button>
              ))}
              {!crms && !err && <span className={classes.small}>Loading…</span>}
            </div>
            {err && <p className={classes.err}>{err}</p>}
            <div className={classes.footer}>
              <button className={`${classes.btn} ${classes.ghost}`} onClick={onClose}>Cancel</button>
              <button className={`${classes.btn} ${classes.ghost}`} onClick={() => { setErr(null); setAsking(true); }}>Don't see your CRM? Request it</button>
            </div>
          </>
        ) : (
          <>
            <h3 className={classes.h3}>Connect {pick.name}</h3>
            <label className={classes.field}><span>{pick.keyLabel}</span><input type='password' autoComplete='off' value={key} onChange={(e) => setKey(e.target.value)} /></label>
            <p className={classes.small}>{pick.keyHint}</p>
            {err && <p className={classes.err}>{err}</p>}
            <div className={classes.footer}>
              <button className={`${classes.btn} ${classes.ghost}`} onClick={() => { setPick(null); setKey(''); setErr(null); }}>Back</button>
              <button className={`${classes.btn} ${classes.gold}`} disabled={busy || !key.trim()} onClick={connect}>{busy ? 'Checking the key…' : 'Connect'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StatTrackerTile;
